import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button, type ButtonVariant } from './Button';
import { errorMessage } from '../api/client';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  variant?: 'dialog' | 'drawer';
  wide?: boolean;
  /** Clicking the backdrop closes (default true). Off for dialogs holding unsaved input. */
  dismissible?: boolean;
};

/**
 * A modal built on the native <dialog>: showModal() makes the rest of the page
 * inert (so focus stays inside), Esc closes it, and focus returns to whatever
 * opened it.
 */
export function Dialog({ open, onClose, title, description, children, footer, variant = 'dialog', wide, dismissible = true }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      opener.current = document.activeElement;
      el.showModal();
      // Focus the first field if there is one, else the dialog itself.
      const target = el.querySelector<HTMLElement>('[autofocus], [data-autofocus], input:not([type=hidden]), select, textarea');
      (target ?? el).focus();
    } else if (!open && el.open) {
      el.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, [onClose]);

  const cls = ['ui-dialog', variant === 'drawer' && 'ui-drawer', wide && 'ui-dialog--wide'].filter(Boolean).join(' ');

  return (
    <dialog
      ref={ref}
      className={cls}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      tabIndex={-1}
      onMouseDown={(e) => {
        // A press that starts on the backdrop (the dialog element itself) closes.
        if (dismissible && e.target === ref.current) onClose();
      }}
    >
      {open && (
        <>
          <header className="ui-dialog__header">
            <div style={{ minWidth: 0 }}>
              <h2 className="ui-dialog__title" id={titleId}>
                {title}
              </h2>
              {description && (
                <p className="ui-dialog__desc" id={descId}>
                  {description}
                </p>
              )}
            </div>
            <Button variant="ghost" size="sm" iconOnly aria-label="Close" onClick={onClose} icon={<X />} />
          </header>
          {children && <div className="ui-dialog__body">{children}</div>}
          {footer && <footer className="ui-dialog__footer">{footer}</footer>}
        </>
      )}
    </dialog>
  );
}

type ConfirmProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<unknown> | void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
};

/** An in-app confirmation. Shows the server's error inline if the action fails. */
export function ConfirmDialog({ open, onClose, onConfirm, title, description, children, confirmLabel, tone = 'danger' }: ConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const variant: ButtonVariant = tone === 'danger' ? 'danger' : 'primary';
  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={variant}
            loading={busy}
            data-autofocus
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
                onClose();
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {error && (
        <div className="ui-callout ui-callout--crit" role="alert">
          <div className="ui-callout__body" style={{ color: 'var(--text)' }}>
            {error}
          </div>
        </div>
      )}
    </Dialog>
  );
}
